import { useState, useMemo } from "react";
import {
	Dialog,
	DialogContent,
	Box,
	Typography,
	IconButton,
	Chip,
	Divider,
	Stack,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import GroupsIcon from "@mui/icons-material/Groups";
import dayjs from "dayjs";
import AttendanceEditor from "./AttendanceEditor";
import { dialogCloseSx, countChipSx, accordionSummarySx } from "./buttonStyles";

const StatTile = ({ icon: Icon, value, label, bg, color, border }) => (
	<Box
		sx={{
			flex: 1,
			minWidth: 0,
			backgroundColor: bg,
			border: `1px solid ${border}`,
			borderRadius: 2,
			px: { xs: 1, sm: 1.5 },
			py: 1.25,
		}}
	>
		<Stack direction="row" spacing={{ xs: 0.75, sm: 1 }} alignItems="center">
			<Icon sx={{ color, fontSize: 18, flexShrink: 0 }} />
			<Box sx={{ minWidth: 0 }}>
				<Typography sx={{ color, lineHeight: 1.1, fontWeight: 700, fontSize: "1rem" }}>{value}</Typography>
				<Typography sx={{ color: "text.secondary", fontSize: { xs: "0.62rem", sm: "0.68rem" } }} noWrap>
					{label}
				</Typography>
			</Box>
		</Stack>
	</Box>
);

/**
 * One training with its roster. Agents marked here are the ones that earn the training badge —
 * the same records that will be mirrored onto usermeta once the backend lands.
 */
const TrainingPanel = ({ training, roster, expanded, onToggle, onMarkAttended, onRemoveAttendance }) => {
	return (
		<Accordion
			expanded={expanded}
			onChange={onToggle}
			disableGutters
			elevation={0}
			sx={{
				// The root is a Paper, which Elementor sizes to its content — the same rule that
				// shrinks the dialog itself. Forced back to a full-width block.
				display: "block !important",
				width: "100% !important",
				maxWidth: "100% !important",
				alignSelf: "stretch",
				border: "1px solid #D6E6F5",
				borderRadius: "8px !important",
				backgroundColor: "#FFFFFF",
				overflow: "hidden",
				"&:before": { display: "none" },
				mb: 1.5,
			}}
		>
			<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
				<SchoolOutlinedIcon fontSize="small" sx={{ color: "#3A7DC9", flexShrink: 0 }} />

				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }} noWrap title={training.title}>
						{training.title}
					</Typography>
					<Typography sx={{ color: "text.secondary", fontSize: "0.7rem" }} noWrap>
						{dayjs(training.date).format("MMM D, YYYY")}
					</Typography>
				</Box>

				<Chip
					size="small"
					label={`${training.attendees.length} attended`}
					sx={{ flexShrink: 0, ...countChipSx, cursor: "inherit", height: 22, fontSize: "0.68rem" }}
				/>
			</AccordionSummary>

			<AccordionDetails sx={{ px: 2, pt: 0, pb: 2, width: "100%" }}>
				<Divider sx={{ mb: 2 }} />

				<AttendanceEditor
					training={training}
					roster={roster}
					onMarkAttended={(agent) => onMarkAttended(training.id, agent)}
					onRemoveAttendance={(agentId) => onRemoveAttendance(training.id, agentId)}
				/>
			</AccordionDetails>
		</Accordion>
	);
};

const TrainingAttendanceDialog = ({ open, onClose, curriculum, roster = [], onMarkAttended, onRemoveAttendance }) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const [expandedId, setExpandedId] = useState(null);

	const trainings = curriculum?.trainings ?? [];

	const { badges, uniqueAgents } = useMemo(() => {
		const agentIds = new Set();
		let total = 0;

		trainings.forEach((training) => {
			total += training.attendees.length;
			training.attendees.forEach((attendee) => agentIds.add(attendee.id));
		});

		return { badges: total, uniqueAgents: agentIds.size };
	}, [trainings]);

	const handleClose = () => {
		setExpandedId(null);
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="sm"
			fullScreen={fullScreen}
			slotProps={{
				paper: {
					sx: fullScreen
						? { borderRadius: 0, height: "100%", maxHeight: "100%", m: 0, width: "100%", maxWidth: "100% !important" }
						: {
								borderRadius: 3,
								height: "88vh",
								maxHeight: 820,
								// Elementor lets the paper size to its content and ignores MUI's own maxWidth="sm",
								// so the sm breakpoint (600px) is pinned here. The width stays margin-aware
								// (MUI's fullWidth rule is calc(100% - 2*margin)) so the paper never overflows.
								m: 4,
								width: "calc(100% - 64px) !important",
								maxWidth: "600px !important",
							},
				},
			}}
		>
			{/* Flex column so the training list absorbs the extra height instead of leaving dead space. */}
			<DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
				{/* Header */}
				<Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 2, backgroundColor: "#F7FBFF", borderBottom: "1px solid #D6E6F5", flexShrink: 0 }}>
					<Stack direction="row" alignItems="flex-start" justifyContent="space-between">
						<Box sx={{ minWidth: 0 }}>
							<Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>Training Attendance</Typography>
							<Typography sx={{ color: "text.secondary", mt: 0.25, fontSize: "0.75rem" }}>
								Mark who attended each session of{" "}
								<Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
									{curriculum?.title}
								</Box>
							</Typography>
						</Box>

						<IconButton onClick={handleClose} size="small" aria-label="Close training attendance dialog" sx={dialogCloseSx}>
							<CloseIcon />
						</IconButton>
					</Stack>

					{/* Metrics — stays a 3-up row on mobile; tiles shrink rather than stacking. */}
					<Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} sx={{ mt: 2 }}>
						<StatTile
							icon={SchoolOutlinedIcon}
							value={trainings.length}
							label="Trainings"
							bg="#EBF5FF"
							border="#B9D9F7"
							color="#1A3F7A"
						/>
						<StatTile icon={WorkspacePremiumIcon} value={badges} label="Badges" bg="#ECFFF7" border="#B7EBD5" color="#0F7A53" />
						<StatTile icon={GroupsIcon} value={uniqueAgents} label="Agents" bg="#FFF6E8" border="#F7DDB0" color="#D98300" />
					</Stack>
				</Box>

				{/* Trainings — grows to fill the taller dialog */}
				<Box sx={{ px: { xs: 2, sm: 3 }, py: 2, flex: 1, width: "100%", minWidth: 0, alignSelf: "stretch", minHeight: 0, overflowY: "auto" }}>
					{trainings.length === 0 ? (
						<Box sx={{ py: 5, textAlign: "center" }}>
							<Typography sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
								This curriculum has no trainings yet — add one from the edit dialog
							</Typography>
						</Box>
					) : (
						trainings.map((training) => (
							<TrainingPanel
								key={training.id}
								training={training}
								expanded={expandedId === training.id}
								onToggle={() => setExpandedId((prev) => (prev === training.id ? null : training.id))}
								roster={roster}
								onMarkAttended={(trainingId, agent) => onMarkAttended(curriculum.id, trainingId, agent)}
								onRemoveAttendance={(trainingId, agentId) => onRemoveAttendance(curriculum.id, trainingId, agentId)}
							/>
						))
					)}
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default TrainingAttendanceDialog;
